import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { EMAIL_VERIFICATION_STATUS } from '@/graphql/account/queries';
import { SEND_VERIFICATION_EMAIL } from '@/graphql/account/mutations';
import { getPendingJobApplication } from '@/apps/verifyEmail/helpers/pendingJobApplication';
import { getPendingMessageDraft } from '@/apps/verifyEmail/helpers/pendingMessageDraft';
import { currentAccountId } from '@/apps/verifyEmail/helpers/sessionAccountStorage';
import { invalidateEmailVerificationStatus } from '@/apps/verifyEmail/helpers/emailVerificationStatus';
import { getSharedApolloClient } from '@/lib/apollo_setup';

export const EMAIL_VERIFICATION_POLL_INTERVAL_MS = 4000;

type VerifyEmailWaitingProps = {
  active: boolean;
  accountEmail: string;
  showPendingNotices: boolean;
};

type VerifyEmailWaitingEmit = (event: 'verified') => void;

type GraphQLErrorLike = {
  message: string;
  extensions?: { error_code?: string };
};

export function useVerifyEmailWaiting(props: VerifyEmailWaitingProps, emit: VerifyEmailWaitingEmit) {
  const apollo = getSharedApolloClient();

  const email = ref('');
  const notification = ref('');
  const showNotification = ref(false);
  const resending = ref(false);
  const pollTimer = ref<ReturnType<typeof setTimeout> | null>(null);
  const hasReportedVerified = ref(false);
  const isActive = ref(props.active);

  const displayedEmail = computed(() => email.value || props.accountEmail || '');

  const showPendingJobApplicationNotice = computed(() => props.showPendingNotices && getPendingJobApplication() != null);

  const showPendingMessageDraftNotice = computed(() => {
    if (!props.showPendingNotices) {
      return false;
    }

    const accountId = currentAccountId();
    if (!accountId) {
      return false;
    }

    return getPendingMessageDraft(accountId) != null;
  });

  watch(
    () => props.active,
    (value) => {
      isActive.value = value;
      if (value) {
        void startPolling();
        return;
      }

      stopPolling();
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    isActive.value = false;
    stopPolling();
  });

  async function startPolling(): Promise<void> {
    await checkVerificationStatus();
    if (!isActive.value || hasReportedVerified.value) {
      return;
    }
    scheduleNextPoll();
  }

  function scheduleNextPoll(): void {
    if (!isActive.value || hasReportedVerified.value) {
      return;
    }
    stopPolling();
    pollTimer.value = setTimeout(() => {
      void pollOnce();
    }, EMAIL_VERIFICATION_POLL_INTERVAL_MS);
  }

  async function pollOnce(): Promise<void> {
    if (!isActive.value) {
      return;
    }
    await checkVerificationStatus();
    if (isActive.value && !hasReportedVerified.value) {
      scheduleNextPoll();
    }
  }

  function stopPolling(): void {
    if (pollTimer.value !== null) {
      clearTimeout(pollTimer.value);
      pollTimer.value = null;
    }
  }

  async function checkVerificationStatus(): Promise<void> {
    if (!isActive.value || hasReportedVerified.value) {
      return;
    }

    try {
      const { data } = await apollo.query({
        query: EMAIL_VERIFICATION_STATUS,
        fetchPolicy: 'network-only',
      });

      if (!isActive.value || hasReportedVerified.value) {
        return;
      }

      const status = data?.emailVerificationStatus;
      if (status?.email) {
        email.value = status.email;
      }

      if (status?.verified) {
        reportVerified();
      }
    } catch (error) {
      console.error('Error checking email verification status:', error);
    }
  }

  function reportVerified(): void {
    if (!isActive.value || hasReportedVerified.value) {
      return;
    }

    hasReportedVerified.value = true;
    stopPolling();
    invalidateEmailVerificationStatus();
    emit('verified');
  }

  async function resendVerificationEmail(): Promise<void> {
    if (resending.value) {
      return;
    }

    resending.value = true;
    try {
      await apollo.mutate({
        mutation: SEND_VERIFICATION_EMAIL,
      });
      notify("Verification email re-sent. Check your email to verify and don't forget to check your spam folder.");
    } catch (error: unknown) {
      const graphqlError = (error as { graphQLErrors?: GraphQLErrorLike[] })?.graphQLErrors?.[0];
      if (graphqlError?.extensions?.error_code === 'rate_limit_exceeded') {
        notify(graphqlError.message);
      } else {
        notify('Something went wrong sending your verification email. Please try again.');
        console.error(error);
      }
    } finally {
      resending.value = false;
    }
  }

  function notify(message: string): void {
    notification.value = message;
    showNotification.value = true;
  }

  return {
    displayedEmail,
    showPendingJobApplicationNotice,
    showPendingMessageDraftNotice,
    notification,
    showNotification,
    resending,
    resendVerificationEmail,
  };
}
